import React, {useEffect, useState} from "react";
import {
  Backdrop,
  Divider,
  Fade,
  IconButton,
  Modal,
  Popover,
  Typography,
  MenuItem,
} from "@material-ui/core";
import AddIcon from '@material-ui/icons/Add';
import MoreVertOutlinedIcon from '@material-ui/icons/MoreVertOutlined';
import { addBalanceModalStyle } from "./AddBalanceModalStyle";
import AddPaymentMethodModal from "./AddPaymentMethodModal";
import {activateCard, deleteCard, existingCardList} from "../../../api/billingApi";
import NewConfirmAlert from "../../Common/NewConfirmAlert";
import CardSkeleton from "./CardListSkeletonLoader";
import {useDispatch} from "react-redux";
import {fetchUserCardInfo} from "../../../actions/billingAction";
import {cardBrandBgPosition, cardBrandLabel, cardExpiry} from "./utils";

const ManageMethodModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = addBalanceModalStyle();
  const [existingCards, setExistingCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(()=>{
    loadData();
  },[]);

  const loadData = async () =>{
    try{
      setLoading(true);
      const res = await existingCardList({});
      if (res?.data){
        const data = res.data;
        if (data.status === 'success'){
          setExistingCards(data.data);
        }
      }
    }catch (err){
      console.log(err);
    }finally {
      setLoading(false);
    }
  };

  const handlePopoverOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedCard(item);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleMakeDefault = () => {
    handlePopoverClose();
    try{
      NewConfirmAlert({
        title: 'Confirm to make default',
        description: 'Do you want to make this card default?',
        cancelText: "Cancel",
        submitText: "Confirm",
        onSubmit: async () => {
          try{
            const response = await activateCard({
              "form_data": {
                card_id: selectedCard.id
              }
            });

            if (response?.data){
              dispatch(fetchUserCardInfo({}));
              await loadData();
              window.showNotification('success', 'Default card updated successfully');
            }else {
              window.showNotification('error', 'Failed to update default card');
            }
          }catch (err){
            console.log(err);
            window.showNotification('error', 'Something went wrong');
          }finally {
            setSelectedCard(null);
          }
        }
      });
    }catch (err){
      console.log(err);
    }
  };

  const handleDelete = () => {
    handlePopoverClose();
    try{
      NewConfirmAlert({
        title: 'Confirm to delete',
        description: 'Do you want to delete this card?',
        cancelText: "Cancel",
        submitText: "Confirm",
        onSubmit: async () => {
          try{
            const response = await deleteCard({
              "form_data": {
                card_id: selectedCard.id
              }
            });

            if (response?.data){
              await loadData();
              window.showNotification('success', 'Card deleted successfully');
            }else {
              window.showNotification('error', 'Failed to delete card');
            }
          }catch (err){
            console.log(err);
          }finally {
            window.showNotification('error', 'Something went wrong');
            setSelectedCard(null);
          }
        }
      });
    }catch (err){
      console.log(err);
    }
  };

  const openPopover = Boolean(anchorEl);

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={open}
        onClose={onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={open}>
          <div className={classes.paper} style={{ width: 660 }}>
            <div className={classes.modalHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <img
                  width={50}
                  src={`${process.env.REACT_APP_CDN_LINK}assets/images/releases/billing/1.0.0/card_icon.png`}
                  alt=""
                />
                <div>
                  <Typography
                    style={{
                      height: "unset !important",
                      color: "black",
                      fontWeight: 600,
                    }}
                    variant="h5"
                  >
                    Manage your payment methods
                  </Typography>
                  <div style={{ fontSize: 16, fontWeight: 300 }}>
                    Set primary card or add/delete cards
                  </div>
                </div>
              </div>
            </div>
            <Divider />

            <div className={classes.modalBody} style={{ minHeight: 220 }}>
              {
                loading ?
                    <CardSkeleton/> :
                    <div className="cardSection">
                      {
                        existingCards.map((existingCard)=>(
                            <div className="eachCard" key={existingCard.id}>
                              <div className="leftSide">
                                <div className="cardLogo">
                                  <div className="logo">
                                    <span className={classes.brandIcon} style={{ backgroundPosition: cardBrandBgPosition(existingCard.card_details) }}></span>
                                  </div>
                                </div>

                                <div className="cardDetails">
                                  <div className="cardNumber">{cardBrandLabel(existingCard.card_details)} **** {existingCard.card_number}</div>
                                  <div className="cardExpireDate">Expires {cardExpiry(existingCard.card_details)}</div>
                                </div>

                                {
                                    existingCard.status === 1 &&
                                    <div className="cardStatus">Primary Card</div>
                                }
                              </div>

                              {
                                  existingCard.status !== 1 &&
                                  <div className="rightSide">
                                    <IconButton size="small" className="moreIcon" onClick={(event) => handlePopoverOpen(event, existingCard)}>
                                      <MoreVertOutlinedIcon />
                                    </IconButton>
                                  </div>
                              }
                            </div>
                        ))
                      }
                    </div>
              }
            </div>

            <div className={classes.modalFooter}>
              <div
                className={classes.actionContainer}
                style={{ display: "flex", alignItems: "center", gap: 16 }}
              >
                <button
                  onClick={handleClose}
                  className={`${classes.button} ${classes.closeButton}`}
                  style={{ padding: "8px 24px" }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowMethodModal(true)}
                  className={`${classes.button} ${classes.confirmButton}`}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <AddIcon style={{ fontSize: 15 }} />
                    <span>Add New Card</span>
                  </div>
                </button>
              </div>
            </div>

            <Popover
              open={openPopover}
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <MenuItem onClick={handleMakeDefault}>Make Default</MenuItem>
              <MenuItem onClick={handleDelete}>Delete</MenuItem>
            </Popover>

            {showMethodModal && (
              <AddPaymentMethodModal
                  open={showMethodModal}
                  onClose={() => setShowMethodModal(false)}
                  loadData={async () =>{
                      await loadData();
                      dispatch(fetchUserCardInfo({}));
                  }}
              />
            )}
          </div>
        </Fade>
      </Modal>
    </>
  );
};

export default ManageMethodModal;
